import React, { useState, useMemo } from 'react';
import { Search, ChevronLeft, ChevronRight, Check, PackageOpen } from 'lucide-react';
import classNames from 'classnames';
import Select from './Select';
import Combobox from './Combobox';

// Generic item type that the table can display
export interface SelectableItem {
	id: string;
	brand: string;
	name: string;
	storage?: number;
	ram?: number;
	price?: number;
}

// Format storage/RAM value: GB with no decimals, convert to MB if under 1GB
export function formatStorageOrRam(value: number | null | undefined): string {
	if (value == null) return '-';
	if (value < 1) {
		return `${Math.round(value * 1024)} MB`;
	}
	return `${Math.round(value)} GB`;
}

interface ProductTableProps {
	items: SelectableItem[];
	selectedIds: Set<string>;
	onSelectionChange: (ids: Set<string>) => void;
	singleSelect?: boolean;
	disabledIds?: Set<string>;
	disabledLabel?: string;
	emptyMessage?: string;
	emptyFilterMessage?: string;
	showPrice?: boolean;
	pageSize?: number;
	loading?: boolean;
}

const PAGE_SIZE_OPTIONS = [5, 10, 15, 20, 25, 50];

export default function ProductTable({
	items,
	selectedIds,
	onSelectionChange,
	singleSelect = false,
	disabledIds = new Set(),
	disabledLabel = 'En inventario',
	emptyMessage = 'No hay elementos disponibles',
	emptyFilterMessage = 'No se encontraron elementos con esos filtros',
	showPrice = false,
	pageSize: defaultPageSize = 15,
	loading = false
}: ProductTableProps) {
	const [search, setSearch] = useState('');
	const [brandFilter, setBrandFilter] = useState<string>('all');
	const [currentPage, setCurrentPage] = useState(0);
	const [pageSize, setPageSize] = useState(defaultPageSize);

	// Get unique brands for filter
	const brandOptions = useMemo(() => {
		const brands = [...new Set(items.map((d) => d.brand).filter(Boolean))].sort();
		return [{ label: 'Todas las marcas', value: 'all' }, ...brands.map((b) => ({ label: b, value: b }))];
	}, [items]);

	// Apply filters
	const filteredItems = useMemo(() => {
		return items.filter((d) => {
			if (brandFilter !== 'all' && d.brand !== brandFilter) return false;
			if (search) {
				const searchLower = search.toLowerCase();
				if (!d.brand.toLowerCase().includes(searchLower) && !d.name.toLowerCase().includes(searchLower)) {
					return false;
				}
			}
			return true;
		});
	}, [items, brandFilter, search]);

	const pageCount = Math.ceil(filteredItems.length / pageSize);
	const paginatedItems = filteredItems.slice(currentPage * pageSize, (currentPage + 1) * pageSize);

	// Generate page options for Select
	const pageOptions = useMemo(() => {
		return Array.from({ length: pageCount }, (_, i) => ({
			label: String(i + 1),
			value: String(i)
		}));
	}, [pageCount]);

	// Generate page size options
	const pageSizeOptions = useMemo(() => {
		return PAGE_SIZE_OPTIONS.map((size) => ({
			label: `${size}`,
			value: String(size)
		}));
	}, []);

	const handleSelect = (id: string) => {
		if (disabledIds.has(id)) return;

		if (singleSelect) {
			// Single select: replace selection with just this item
			onSelectionChange(new Set([id]));
		} else {
			// Multi select: toggle selection
			const next = new Set(selectedIds);
			if (next.has(id)) {
				next.delete(id);
			} else {
				next.add(id);
			}
			onSelectionChange(next);
		}
	};

	const clearSelection = () => {
		onSelectionChange(new Set());
	};

	return (
		<div className="flex flex-col min-h-0 flex-1">
			{/* Filters */}
			<div className="shrink-0 flex items-center gap-3 pb-4">
				<div className="relative flex-1">
					<Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-neutral-500 pointer-events-none" />
					<input
						type="text"
						placeholder="Buscar..."
						value={search}
						onChange={(e) => {
							setSearch(e.target.value);
							setCurrentPage(0);
						}}
						className="w-full h-9 pl-9 pr-3 rounded-lg bg-neutral-900 border border-neutral-800 text-sm text-neutral-100 placeholder:text-neutral-500 focus:outline-none focus:border-neutral-700 transition-colors"
					/>
				</div>
				<Combobox
					options={brandOptions}
					defaultValue={brandFilter}
					placeholder="Marca"
					onSelectedChange={(value) => {
						setBrandFilter((value as string) || 'all');
						setCurrentPage(0);
					}}
					className="!h-9 !text-xs !bg-neutral-900/50 shrink-0 min-w-[140px]"
					isDefaultValueNull={false}
					emptyMessage="No se encontró la marca"
				/>
			</div>

			{/* Selection info (multi-select only) */}
			{!singleSelect && selectedIds.size > 0 && (
				<div className="shrink-0 flex items-center justify-between px-3 py-2 mb-4 bg-primary-500/10 border border-primary-500/20 rounded-lg">
					<span className="text-sm text-primary-300">
						{selectedIds.size} {selectedIds.size === 1 ? 'seleccionado' : 'seleccionados'}
					</span>
					<button onClick={clearSelection} className="text-xs text-primary-400 hover:text-primary-300">
						Limpiar selección
					</button>
				</div>
			)}

			{/* Table */}
			{loading && items.length === 0 ? (
				<div className="min-h-0 flex-1 rounded-lg border border-neutral-800 flex flex-col overflow-hidden animate-pulse">
					{/* Header skeleton */}
					<div className="shrink-0 bg-neutral-900 border-b border-neutral-800 flex px-4 py-2.5 gap-4">
						{!singleSelect && <div className="w-5 h-3 rounded bg-neutral-800/60" />}
						<div className="flex-1 h-3 rounded bg-neutral-800/60 max-w-[40px]" />
						<div className="flex-[2] h-3 rounded bg-neutral-800/60 max-w-[50px]" />
						<div className="w-20 h-3 rounded bg-neutral-800/60 max-w-[40px]" />
						<div className="w-20 h-3 rounded bg-neutral-800/60 max-w-[30px]" />
						{showPrice && <div className="w-24 h-3 rounded bg-neutral-800/60 max-w-[40px]" />}
					</div>
					{/* Row skeletons */}
					{[...Array(Math.min(defaultPageSize, 8))].map((_, i) => (
						<div
							key={i}
							className="flex px-4 py-3 gap-4 border-b border-neutral-800/50 last:border-b-0"
							style={{ opacity: 1 - i * 0.1 }}
						>
							{!singleSelect && <div className="w-5 h-5 rounded border-2 border-neutral-700" />}
							<div className="flex-1 h-4 rounded bg-neutral-800/50" style={{ maxWidth: `${50 + (i % 3) * 10}px` }} />
							<div className="flex-[2] h-4 rounded bg-neutral-800/40" style={{ maxWidth: `${90 + (i % 4) * 20}px` }} />
							<div className="w-20 h-4 rounded bg-neutral-800/35 max-w-[50px]" />
							<div className="w-20 h-4 rounded bg-neutral-800/35 max-w-[40px]" />
							{showPrice && <div className="w-24 h-4 rounded bg-neutral-800/30 max-w-[55px]" />}
						</div>
					))}
				</div>
			) : items.length === 0 ? (
				<div className="shrink-0 p-8 text-center text-sm text-neutral-500 border border-neutral-800 rounded-lg bg-neutral-900/50">
					<PackageOpen className="size-10 text-neutral-700 mx-auto mb-2" />
					{emptyMessage}
				</div>
			) : filteredItems.length === 0 ? (
				<div className="shrink-0 p-8 text-center text-sm text-neutral-500 border border-neutral-800 rounded-lg bg-neutral-900/50">
					<Search className="size-10 text-neutral-700 mx-auto mb-2" />
					{emptyFilterMessage}
				</div>
			) : (
				<div className="min-h-0 flex-1 rounded-lg border border-neutral-800 flex flex-col overflow-hidden">
					{/* Table header */}
					<div className="shrink-0 bg-neutral-900 border-b border-neutral-800 flex">
						{!singleSelect && <div className="w-12 shrink-0 pl-4 pr-2 py-2.5" />}
						<div
							className={classNames(
								'flex-1 px-3 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wider text-neutral-400',
								singleSelect && 'pl-4'
							)}
						>
							Marca
						</div>
						<div className="flex-[2] px-3 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wider text-neutral-400">
							Modelo
						</div>
						<div className="w-20 px-3 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wider text-neutral-400">
							Almac.
						</div>
						<div className="w-20 px-3 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wider text-neutral-400">
							RAM
						</div>
						{showPrice && (
							<div className="w-24 px-3 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wider text-neutral-400">
								Precio
							</div>
						)}
					</div>

					{/* Scrollable rows */}
					<div className="min-h-0 flex-1 overflow-y-auto overscroll-contain">
						{paginatedItems.map((item) => {
							const isSelected = selectedIds.has(item.id);
							const isDisabled = disabledIds.has(item.id);
							return (
								<div
									key={item.id}
									onClick={() => handleSelect(item.id)}
									className={classNames(
										'flex transition-colors border-b border-neutral-800/50 last:border-b-0',
										isDisabled
											? 'cursor-not-allowed opacity-50'
											: isSelected
												? 'cursor-pointer bg-primary-500/10 hover:bg-primary-500/15'
												: 'cursor-pointer hover:bg-neutral-800/20'
									)}
								>
									{!singleSelect && (
										<div className="w-12 shrink-0 pl-4 pr-2 py-2.5 flex items-center">
											<div
												className={classNames(
													'w-5 h-5 rounded border-2 flex items-center justify-center transition-colors',
													isDisabled
														? 'border-neutral-700 bg-neutral-800'
														: isSelected
															? 'bg-primary-500 border-primary-500'
															: 'border-neutral-600'
												)}
											>
												{isSelected && !isDisabled && <Check className="size-3 text-white" />}
											</div>
										</div>
									)}
									<div
										className={classNames(
											'flex-1 px-3 py-2.5 text-sm font-medium text-neutral-100',
											singleSelect && 'pl-4'
										)}
									>
										{item.brand}
									</div>
									<div className="flex-[2] px-3 py-2.5 text-sm text-neutral-400 flex items-center gap-2">
										{item.name}
										{isDisabled && (
											<span className="text-[10px] px-1.5 py-0.5 rounded bg-neutral-800 text-neutral-500 font-medium">
												{disabledLabel}
											</span>
										)}
									</div>
									<div className="w-20 px-3 py-2.5 text-sm text-neutral-400">{formatStorageOrRam(item.storage)}</div>
									<div className="w-20 px-3 py-2.5 text-sm text-neutral-400">{formatStorageOrRam(item.ram)}</div>
									{showPrice && (
										<div className="w-24 px-3 py-2.5 text-sm text-neutral-100 font-medium">
											{item.price != null
												? `$${item.price.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
												: '-'}
										</div>
									)}
								</div>
							);
						})}
					</div>

					{/* Pagination */}
					{pageCount > 1 && (
						<div className="shrink-0 flex items-center justify-between px-4 py-2.5 border-t border-neutral-800 bg-neutral-900/50">
							<div className="flex items-center gap-2">
								<span className="text-xs text-neutral-500">
									{currentPage * pageSize + 1}-{Math.min((currentPage + 1) * pageSize, filteredItems.length)} de{' '}
									{filteredItems.length}
								</span>
								<Select
									options={pageSizeOptions}
									defaultValue={String(pageSize)}
									onSelectedChange={(value) => {
										const newSize = Number(value);
										setPageSize(newSize);
										setCurrentPage(0);
									}}
									className="!h-7 !text-xs !bg-neutral-900 !min-w-[90px]"
								/>
							</div>
							<div className="flex items-center gap-1">
								<button
									onClick={() => setCurrentPage((p) => Math.max(0, p - 1))}
									disabled={currentPage === 0}
									className="p-1.5 rounded-md hover:bg-neutral-800 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
								>
									<ChevronLeft className="size-4" />
								</button>
								<Select
									options={pageOptions}
									defaultValue={String(currentPage)}
									onSelectedChange={(value) => setCurrentPage(Number(value))}
									className="!h-7 !text-xs !bg-neutral-900 !min-w-[50px] !w-[50px]"
								/>
								<span className="text-xs text-neutral-500">/ {pageCount}</span>
								<button
									onClick={() => setCurrentPage((p) => Math.min(pageCount - 1, p + 1))}
									disabled={currentPage >= pageCount - 1}
									className="p-1.5 rounded-md hover:bg-neutral-800 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
								>
									<ChevronRight className="size-4" />
								</button>
							</div>
						</div>
					)}
				</div>
			)}
		</div>
	);
}
